import React from "react";
import { VstPopover } from "vstui";
import PropTypes from "prop-types";
import Dispatcher from "../../../dispatcher";
import HighlightStore from "../../../stores/highlight-store";
import HighlighterActions from "../../../actions/highlight-action";
import MarkerStore from "../../../stores/marker-store";
import HighlightAction from "../../../actions/highlight-action";
import FractionStore from "../../../stores/fraction-store";
import BookStore from "../../../stores/book-store";
import Isvg from "../../../../vendor/react-inlinesvg";
import DeckList from "./deck-list";
import HighligherColors from "./highlighter-colors";
import HighlighterNotes from "./highlighter-notes";
import EmojiReactions from "./emoji-reactions";
import startIcon from "../tts/startIcon";
import gadgets from "../../gadgets/gadgets";
import styled from "styled-components";
import { EMOJI_MARKER_LABEL } from "../../../redux/markers/constants";
import { isReaction, getEmoji, isEmojiMarker } from "./emoji-reactions/helpers";
import withChrome from "../with-chrome";

const ReadAloudIcon = styled(startIcon)`
  margin: 2px 8px 0 0;
`;

const isClickable = () => {
  return (
    !FractionStore.get().highlightClickingDisabled &&
    !Fraction.Helpers.isTouchDevice() &&
    !Fraction.Helpers.isSmallScreen()
  );
};

const calculatePosition = rect => {
  const style = {};
  const reader = $("#jigsaw-placeholder-outer").offset();
  const boxHeight = document.getElementById("highlight-popover").offsetHeight;
  const boxWidth = document.getElementById("highlight-popover").offsetWidth;

  style.left = rect.left + reader.left;
  style.top = rect.top + rect.height + reader.top;

  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  // Overflows bottom
  if (style.top + boxHeight > windowHeight) {
    style.top = style.top - rect.height - boxHeight;
  }

  // Overflows top
  if (style.top < 0) {
    style.top = 10;
  }

  // Overflows right
  if (style.left + boxWidth > windowWidth) {
    style.left = "unset";
    style.right = 10;
  }

  if (style.left < 0) {
    style.left = reader.left + 10;
  }

  return style;
};

class HighlightPopover extends React.Component {
  constructor(props) {
    super(props);

    this.defaultState = {
      open: false,
      hasOverlay: false,
      popoverStyle: {},
      highlight: null,
      currentSelectedText: "",
      noteText: "",
      isEditingNote: false
    };

    this.state = this.defaultState;
    this.showPopover = this.showPopover.bind(this);
    this.hidePopover = this.hidePopover.bind(this);
    this.onHighlightClick = this.onHighlightClick.bind(this);
    this.onTextSelect = this.onTextSelect.bind(this);
    this.onBookClick = this.onBookClick.bind(this);
    this.clearHoverTimeout = this.clearHoverTimeout.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
    this.showOverlay = this.showOverlay.bind(this);
    this.hideOverlay = this.hideOverlay.bind(this);
    this.onGadgetClick = this.onGadgetClick.bind(this);
    this.onCopyClick = this.onCopyClick.bind(this);
    this.highlightUpdated = this.highlightUpdated.bind(this);
    this.checkIfOverflowing = this.checkIfOverflowing.bind(this);
    this.emojiClick = this.emojiClick.bind(this);
    this.setEditing = this.setEditing.bind(this);
    this.addHighlight = this.addHighlight.bind(this);
    this.onNoteTextChange = this.onNoteTextChange.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if ((this.state.data || this.state.highlight) && this.state.open) {
      const needToMoveMenuUp = this.checkIfOverflowing();
      if (needToMoveMenuUp) {
        this.setState({
          data: this.state.data
            ? Object.assign(this.state.data, needToMoveMenuUp)
            : needToMoveMenuUp,
          popoverStyle: Object.assign(this.state.popoverStyle, needToMoveMenuUp)
        });
      }
    }

    if (!prevState.isEditingNote && this.state.isEditingNote) {
      this.showOverlay();
    }
  }

  componentDidMount() {
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() + 11);
    Dispatcher.bind(Dispatcher.BOOK_HIGHLIGHT_CLICK, this.onHighlightClick);
    Dispatcher.bind(Dispatcher.BOOK_NOTEICON_CLICK, this.onHighlightClick);
    Dispatcher.bind(Dispatcher.BOOK_SELECTION_WITH_POSITION, this.onTextSelect);
    Dispatcher.bind(Dispatcher.BOOK_CLICK, this.onBookClick);
    Dispatcher.bind(Dispatcher.MODAL_OPEN, this.hidePopover);
    Dispatcher.bind(Dispatcher.BOOK_NEXT_PAGE, this.hidePopover);
    Dispatcher.bind(Dispatcher.BOOK_PREVIOUS_PAGE, this.hidePopover);
    Dispatcher.bind(Dispatcher.BOOK_PAGE_SCROLL_FULLSPEED, this.hidePopover);
    Dispatcher.bind(Dispatcher.BOOK_NAVIGATION_REQUESTED, this.hidePopover);
    Dispatcher.bind(Dispatcher.HIGHLIGHT_UPDATED, this.highlightUpdated);
    Dispatcher.bind(Dispatcher.HIGHLIGHT_ADDED, this.highlightUpdated);
    Dispatcher.bind(Dispatcher.BOOK_IMAGE_BUTTON_CLICK, this.hidePopover);
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.BOOK_HIGHLIGHT_CLICK, this.onHighlightClick);
    Dispatcher.unbind(Dispatcher.BOOK_NOTEICON_CLICK, this.onHighlightClick);
    Dispatcher.unbind(
      Dispatcher.BOOK_SELECTION_WITH_POSITION,
      this.onTextSelect
    );
    Dispatcher.unbind(Dispatcher.BOOK_CLICK, this.onBookClick);
    Dispatcher.unbind(Dispatcher.MODAL_OPEN, this.hidePopover);
    Dispatcher.unbind(Dispatcher.BOOK_NEXT_PAGE, this.hidePopover);
    Dispatcher.unbind(Dispatcher.BOOK_PREVIOUS_PAGE, this.hidePopover);
    Dispatcher.unbind(Dispatcher.BOOK_PAGE_SCROLL_FULLSPEED, this.hidePopover);
    Dispatcher.unbind(Dispatcher.BOOK_NAVIGATION_REQUESTED, this.hidePopover);
    Dispatcher.unbind(Dispatcher.BOOK_IMAGE_BUTTON_CLICK, this.hidePopover);
    Dispatcher.unbind(Dispatcher.HIGHLIGHT_UPDATED, this.highlightUpdated);
    Dispatcher.unbind(Dispatcher.HIGHLIGHT_ADDED, this.highlightUpdated);
    Dispatcher.setMaxListeners(Dispatcher.getMaxListeners() - 11);
  }

  checkIfOverflowing() {
    const style = {};
    const rect = this.state.data ? this.state.data : this.state.popoverStyle;
    const reader = $("#jigsaw-placeholder-outer").offset();
    const boxHeight = document.getElementById("highlight-popover").offsetHeight;
    const windowHeight = window.innerHeight + reader.top;
    style.top = rect.top + reader.top;

    if (style.top + boxHeight > windowHeight) {
      style.top = style.top - boxHeight - (this.state.data ? 0 : 50);
      return style;
    } else if (style.top < 0) {
      style.top = 10;
      return style;
    }
    return false;
  }

  showPopover(isbn, e, data) {
    this.clearHoverTimeout();
    const highlight = HighlightStore.get(this.props.isbn, data.syncId) || null;
    const show = () => {
      Dispatcher.emit(Dispatcher.POPOVER_OPEN);
      this.setState(
        state => ({
          highlight,
          noteText: highlight ? highlight.noteText : "",
          open: true,
          savingNote: false,
          returnCFI: data.cfi,
          data: data.selectionRect
        }),
        () => {
          const position = calculatePosition(
            data.selectionRect || data.position
          );
          this.setState(prevState => ({
            popoverStyle: Object.assign(prevState.popoverStyle, position)
          }));

          // remove for now because it breaks keyboard copy of text
          //this.popover.focus();
        }
      );
    };

    if (
      this.state.open &&
      this.state.highlight &&
      this.state.highlight.syncId !== data.syncId
    ) {
      this.setState({ open: false }, show);
    } else {
      show();
    }
  }

  highlightUpdated(isbn, highlight) {
    this.addingHighlight = false;

    if (
      !this.state.highlight ||
      highlight.syncId === this.state.highlight.syncId
    ) {
      this.setState({
        highlight,
        noteText: highlight.noteText,
        savingNote: false
      });
    }
  }

  saveNote() {
    if (this.state.open) {
      const { highlight, noteText } = this.state;
      if (!highlight && noteText && noteText.length > 0) {
        this.addHighlight(noteText);
        this.setState({ isEditingNote: false, savingNote: true });
      } else if (highlight && noteText !== highlight.noteText) {
        HighlightAction.updateHighlight(this.props.isbn, highlight.syncId, {
          noteText
        });
        this.setState({ isEditingNote: false, savingNote: true });
      }
    }
  }

  hidePopover() {
    this.saveNote();
    this.addingHighlight = false;
    if (this.state.open && this.state.returnCFI) {
      // remove for now because we're not currently stealing focus from the book
      // const book = BookStore.get(this.props.isbn);
      // book.goTo({cfi: book.pageCFI + "!" + this.state.returnCFI});
    }
    this.clearHoverTimeout();
    this.setState(this.defaultState);
    Dispatcher.emit(Dispatcher.POPOVER_CLOSE);
  }

  clearHoverTimeout() {
    clearTimeout(this.hoverTimeout);
  }

  showOverlay() {
    this.setState({ hasOverlay: true });
  }

  hideOverlay() {
    this.saveNote();
    this.setState({ hasOverlay: false });
  }

  onBookClick(isbn, e, data) {
    this.hidePopover();
  }

  onHighlightClick(isbn, e, data) {
    this.showPopover(isbn, e, data);
  }

  onTextSelect(isbn, e, data) {
    if (Fraction.Brand.Reader.highlighting) {
      if (
        Fraction.Helpers.isTouchDevice() ||
        Fraction.Helpers.isSmallScreen()
      ) {
        return; // do not show main highlight container for touch devices
      }
      const notesDown = FractionStore.get()["notes-down"];
      if (this.props.fastHighlighting && !notesDown) {
        if (MarkerStore.getCurrentMarker()) {
          HighlightAction.addHighlight(this.props.isbn);
        } else {
          Fraction.Helpers.toast(i18n.t("reader.notebook.please-wait-loading"));
        }
      } else {
        if (isClickable()) {
          const { selectedText } = data;
          this.setState(
            {
              currentSelectedText: selectedText,
              highlight: null,
              noteText: ""
            },
            () => this.showPopover(isbn, e, data)
          );
        }
        Fraction.Reporter.gaEvent(
          "V3/highlights",
          "book:selectionWithPosition",
          "newHighlightPopup"
        );
      }
    }
  }

  onBackdropClick() {
    if (this.state.hasOverlay) {
      this.hideOverlay();
    } else {
      this.hidePopover();
    }
  }

  onGadgetClick(metadata) {
    metadata.action(this.props.isbn, Object.assign(this.state, this.props));
    if (metadata.resetHighlightData) {
      this.setState(this.defaultProps);
    }
  }

  onCopyClick() {
    const selectedText =
      this.state.currentSelectedText ||
      (this.state.highlight && this.state.highlight.selectedText);
    if (Fraction.Helpers.copyText(selectedText)) {
      Fraction.Reporter.gaEvent("reader/highlights/popup/copy");
      if (this.state.highlight) {
        Fraction.Helpers.toast(i18n.t("highlighters.highlight-copied"));
      } else {
        Fraction.Helpers.toast(i18n.t("highlighters.selection-copied"));
      }
    } else {
      Fraction.Helpers.toast(i18n.t("highlighters.could-not-copy"));
    }
    this.hidePopover();
  }

  addHighlight(text) {
    if (!this.addingHighlight) {
      this.addingHighlight = true;
      HighlightAction.addHighlight(this.props.isbn, text);
    }
  }

  addEmojiHighlight(emoji, marker) {
    if (!this.addingHighlight) {
      this.addingHighlight = true;
      HighlightAction.addEmojiHighlight(this.props.isbn, emoji, marker);
      this.hidePopover();
    }
  }

  setEditing(isEditingNote) {
    this.setState({ isEditingNote });
  }

  onNoteTextChange(e) {
    if (e && e.markdown) {
      this.setState({ noteText: e.markdown });
    } else {
      this.setState({ noteText: e.currentTarget.value });
    }
  }

  createEmojiMarker(emoji) {
    Jigsaw.User.createMarker({ label: EMOJI_MARKER_LABEL }, response => {
      if (response.isSuccess()) {
        this.addEmojiHighlight(emoji, response.data);
        HighlighterActions.resetHighlights(this.props.isbn);
      }
    });
  }

  emojiClick(emoji, deleteEmoji) {
    let marker = MarkerStore.getAll().markers.find(isEmojiMarker);

    if (!marker) {
      this.createEmojiMarker(emoji);
    } else {
      const { highlight } = this.state;

      if (deleteEmoji) {
        HighlightAction.deleteHighlight(this.props.isbn, highlight.syncId);
        this.setState({ open: false });
        return;
      }

      if (highlight) {
        HighlightAction.updateHighlight(this.props.isbn, highlight.syncId, {
          noteText: emoji,
          markerId: marker.globalId
        });
        this.setState({ open: false });
      } else {
        this.addEmojiHighlight(emoji, marker);
      }
    }
  }

  renderGadgets() {
    const { isbn } = this.props;
    return gadgets
      .map(gadget => gadget.metadata())
      .filter(
        metadata =>
          metadata.type === "context" && metadata.enabled(isbn, this.state)
      )
      .map(metadata => (
        <VstPopover.Item
          onClick={() => this.onGadgetClick(metadata)}
          onTouchEnd={() => this.onGadgetClick(metadata)}
          key={metadata.id}
        >
          {metadata.name}
        </VstPopover.Item>
      ));
  }

  renderCopyButton() {
    const book = BookStore.get(this.props.isbn);
    if (
      Fraction.Helpers.copyEnabled() &&
      (book.copyLimit == null || book.copyLimit > 0)
    ) {
      // NOTE:: this must be == instead of === in case copyLimit is undefined
      return (
        <VstPopover.Item onClick={this.onCopyClick}>
          {i18n.t("reader.notebook.copy")}
        </VstPopover.Item>
      );
    }
  }

  renderDeckCreate() {
    if (this.props.chromeId) {
      return null;
    }

    let gadget = gadgets.find(gadget => gadget.metadata().id === "flashcards");
    if (gadget.metadata().enabled()) {
      const currentSelectedText =
        this.state.currentSelectedText ||
        (this.state.highlight && this.state.highlight.selectedText);
      if (currentSelectedText) {
        return (
          <VstPopover.Item
            role="select"
            id="deck-list"
            text={i18n.t("reader.flashcards.create-card")}
            closeOnClick={false}
            isList={false}
          >
            <DeckList
              isbn={this.props.isbn}
              selectedText={currentSelectedText}
              onClick={this.showOverlay}
              closePopover={this.hidePopover}
            />
          </VstPopover.Item>
        );
      }
    }
  }

  renderSharedMarker() {
    const { highlight } = this.state;
    if (highlight && highlight.shared) {
      const marker = MarkerStore.get(highlight.markerId);
      const style = {
        border: `1px solid ${marker.vstColorDark}`,
        backgroundColor: marker.vstColor
      };
      return (
        <div className="shared-highlighter">
          <div className="circle" style={style}>
            <Isvg src="/img/public_icon.svg" />
          </div>
          <div>{marker.label}</div>
        </div>
      );
    }
  }

  readAloudStart() {
    const ttsEnabled = window.hasOwnProperty("speechSynthesis") && Fraction.Brand.TTS.enabled;
    if (ttsEnabled) {
      return (
        <VstPopover.Item
          onClick={() => {
            const book = BookStore.get(this.props.isbn);
            if (this.state.highlight) {
              book.viewer.TTS.startFromHighlight(this.state.highlight.syncId);
            } else {
              book.viewer.TTS.startFromSelection();
            }
            Dispatcher.emit(Dispatcher.TTS_OPEN);
            this.hidePopover();
          }}
        >
          <ReadAloudIcon />
          {i18n.t("tts.context-start")}
        </VstPopover.Item>
      );
    }
  }

  renderNoteArea() {
    const { isEditingNote, highlight, savingNote } = this.state;
    if (highlight) {
      const marker = MarkerStore.get(highlight.markerId);
      if (isEmojiMarker(marker)) {
        return null;
      }
    }

    return (
      <HighlighterNotes
        isbn={this.props.isbn}
        isEditing={isEditingNote}
        savingNote={savingNote}
        setEditing={this.setEditing}
        highlight={highlight}
        noteText={this.state.noteText}
        onNoteTextChange={this.onNoteTextChange}
      />
    );
  }

  renderHighlightColors() {
    const { highlight } = this.state;
    const highlightColors = (
      <HighligherColors
        isbn={this.props.isbn}
        closePopover={this.hidePopover}
        updateHighlight={!!highlight}
        currentHighlight={highlight}
        currentNoteText={this.state.noteText}
        addHighlight={this.addHighlight}
        resetHighlight={() => this.setState({ highlight: false, noteText: "" })}
      />
    );

    if (!highlight) {
      return highlightColors;
    }

    const marker = MarkerStore.get(highlight.markerId);
    if (!isEmojiMarker(marker)) {
      if (highlight.shared) {
        return (
          <p className="highlight-author">
            <span
              className="shared-by"
              style={{ color: highlight.vstColorDark }}
            >
              {i18n.t("reader.notebook.shared-by")}:
            </span>
            <span>{highlight.authorName}</span>
          </p>
        );
      }
      return highlightColors;
    }
    return null;
  }

  renderReactions() {
    const { noteText, highlight } = this.state;
    if (!highlight) {
      return <EmojiReactions emojiClick={this.emojiClick} />;
    }
    const marker = MarkerStore.get(highlight.markerId);
    if (isEmojiMarker(marker) && isReaction(noteText)) {
      return (
        <EmojiReactions
          currentEmoji={getEmoji(noteText)}
          emojiClick={this.emojiClick}
        />
      );
    }
    return null;
  }

  renderMenu() {
    return (
      <div>
        <h3
          tabIndex={-1}
          ref={pop => {
            this.popover = pop;
          }}
          className="offScreen"
        >
          {this.state.highlight
            ? i18n.t("highlighters.context-menu-edit-highlight")
            : i18n.t("highlighters.context-menu")}
        </h3>
        {this.renderHighlightColors()}
        {this.renderReactions()}
        {this.renderSharedMarker()}
        {this.renderNoteArea()}
        <ul>
          {this.renderCopyButton()}
          {this.renderDeckCreate()}
          {this.renderGadgets()}
          {this.readAloudStart()}
        </ul>
      </div>
    );
  }

  render() {
    const { open, hasOverlay, popoverStyle } = this.state;
    const displayStyle = { display: open ? "block" : "none" };
    if (this.state.open) {
      return (
        <VstPopover
          id="highlight-popover"
          data-testid="highlight-popover"
          className="highlight-popover"
          style={Object.assign(displayStyle, popoverStyle)}
          onBackdropClick={this.onBackdropClick}
          showOverlay={hasOverlay}
          open
        >
          {this.renderMenu()}
        </VstPopover>
      );
    } else {
      return <span />;
    }
  }
}

HighlightPopover.propTypes = {
  isbn: PropTypes.string.isRequired
};

HighlightPopover.defaultProps = {};

export default withChrome(HighlightPopover);
